import PropTypes from 'prop-types'
import {
  Box,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'

import { getFieldProps } from '../../../../common/formProps'
import { STEPS } from '../../../../common/constants'
import useTranslations from '../../../../i18n/useTranslations'

import Autocomplete from '../../../../ui/components/autocomplete'
import useStyles from '../../../../ui/views/shippingSend/styles'
import countryTelephoneData from 'country-telephone-data'

const AddressForm = ({
  isReadOnly,
  values,
  fieldErrors,
  touched,
  onChange,
  onBlur,
  onChangeAutocompleteAddress,
  onTouchAutocompleteAddress,
  currentStep,
  isSmall,
  isSaveAddressSuccess,
  countries,
}) => {
  const t = useTranslations()
  const classes = useStyles()

  const countryPhones = countryTelephoneData.allCountries
    .sort((a, b) => {
      if (a.iso2 === 'es') return -1
      if (b.iso2 === 'es') return 1
      if (a.iso2 === 'pt') return -1
      if (b.iso2 === 'pt') return 1
      return a.dialCode - b.dialCode
    })
    .map((country, index, arr) => {
      if (
        index === arr.length - 1 ||
        country.dialCode !== arr[index + 1].dialCode
      ) {
        return {
          dialCode: `+${country.dialCode}`,
          countryCode: country.iso2,
        }
      }
      return null
    })
    .filter(country => country !== null)

  // const countryRestrictions = getGPlacesRestrictionsForAddressType(addressType)

  return (
    <Box className={classes.fields}>
      <TextField
        name="contactClientCode"
        label={t('noun:contact-client-code')}
        {...getFieldProps('contactClientCode', values, fieldErrors, touched, t)}
        onChange={onChange}
        onBlur={onBlur}
        className={isSmall ? 'input-full' : 'input-medium'}
        color={isSaveAddressSuccess && values.name ? 'success' : null}
        inputProps={{ readOnly: isReadOnly, maxLength: 50 }}
      />
      <TextField
        name="name"
        label={t('noun:name-company')}
        {...getFieldProps('name', values, fieldErrors, touched, t)}
        onChange={onChange}
        onBlur={onBlur}
        className={isSmall ? 'input-full' : 'input-medium'}
        InputLabelProps={{ required: true }}
        color={isSaveAddressSuccess && values.name ? 'success' : null}
        inputProps={{ readOnly: isReadOnly, maxLength: 50 }}
      />

      <Box sx={classes.fieldPrefixPhone}>
        <InputLabel className={classes.labelPrefixPhone} required={true}>
          {t('noun:mobile-phone')}
        </InputLabel>
        <Select
          name="phonePrefix"
          id="phonePrefix"
          defaultValue="+34"
          sx={classes.fieldPrefixPhoneSelect}
          onChange={onChange}
          {...getFieldProps('phonePrefix', values, fieldErrors, touched, t)}
        >
          {countryPhones.map(countryPhone => (
            <MenuItem value={countryPhone.dialCode}>
              {countryPhone.dialCode}
            </MenuItem>
          ))}
        </Select>
        <TextField
          name="phone"
          type="tel"
          {...getFieldProps('phone', values, fieldErrors, touched, t)}
          onChange={onChange}
          onBlur={onBlur}
          className={classes.inputPhone}
          InputLabelProps={{ required: true }}
          color={isSaveAddressSuccess && values.phone ? 'success' : null}
          inputProps={{ readOnly: isReadOnly, maxLength: 13 }}
        />
      </Box>

      <TextField
        name="email"
        label={t('noun:email')}
        type="email"
        {...getFieldProps('email', values, fieldErrors, touched, t)}
        onChange={onChange}
        onBlur={onBlur}
        className={isSmall ? classes.inputEmailInSmallForm : 'input-medium'}
        color={isSaveAddressSuccess && values.email ? 'success' : null}
        inputProps={{ readOnly: isReadOnly }}
      />
      {isReadOnly ? (
        <TextField
          name="autocompleteAddress"
          label={t('noun:postal-address')}
          value={values.autocompleteAddress}
          className={classes.inputAddress}
          inputProps={{ readOnly: isReadOnly }}
          InputLabelProps={{ required: true }}
          color={
            isSaveAddressSuccess && values.autocompleteAddress
              ? 'success'
              : null
          }
        />
      ) : (
        // <GPlacesTextField
        //   values={values}
        //   initialInputValue={values.autocompleteAddress}
        //   onChange={onChange}
        //   onChangeAddress={onChangeAutocompleteAddress}
        //   onTouchAddress={onTouchAutocompleteAddress}
        //   fieldErrors={fieldErrors}
        //   touched={touched}
        //   countryRestrictions={countryRestrictions}
        //   isFullWidth={isSmall}
        //   color={
        //     isSaveAddressSuccess && values.autocompleteAddress
        //       ? 'success'
        //       : null
        //   }
        //   isRequired
        // />
        <div>
          <TextField
            name="autocompleteAddress"
            label={t('noun:route')}
            {...getFieldProps(
              'autocompleteAddress',
              values,
              fieldErrors,
              touched,
              t,
            )}
            onChange={onChange}
            onBlur={onBlur}
            className={isSmall ? 'input-full' : 'input-medium'}
            color={
              isSaveAddressSuccess && values.autocompleteAddress
                ? 'success'
                : null
            }
            InputLabelProps={{ required: true }}
            inputProps={{ readOnly: isReadOnly }}
          />
          <TextField
            name="comments"
            label={t('shipping-send-form:label:additional-comments')}
            {...getFieldProps('comments', values, fieldErrors, touched)}
            onChange={onChange}
            onBlur={onBlur}
            className={isSmall ? 'input-full' : 'input-medium'}
            color={
              isSaveAddressSuccess && values.autocompleteAddress
                ? 'success'
                : null
            }
            inputProps={{ readOnly: isReadOnly }}
          />
          <TextField
            name="postalCode"
            label={t('noun:postal-code')}
            {...getFieldProps('postalCode', values, fieldErrors, touched, t)}
            onChange={onChange}
            onBlur={onBlur}
            className={isSmall ? 'input-small' : 'input-micro'}
            color={isSaveAddressSuccess && values.postalCode ? 'success' : null}
            inputProps={{ readOnly: isReadOnly }}
            InputLabelProps={{ required: true }}
          />
          <TextField
            name="city"
            label={t('noun:city')}
            {...getFieldProps('city', values, fieldErrors, touched, t)}
            onChange={onChange}
            onBlur={onBlur}
            className={isSmall ? 'input-small' : 'input-micro'}
            color={isSaveAddressSuccess && values.city ? 'success' : null}
            InputLabelProps={{ required: true }}
            inputProps={{ readOnly: isReadOnly }}
          />
          {/* <TextField
            name="country"
            label={t('noun:country')}
            {...getFieldProps('country', values, fieldErrors, touched)}
            onChange={onChange}
            onBlur={onBlur}
            className={isSmall ? 'input-small' : 'input-micro'}
            color={isSaveAddressSuccess && values.country ? 'success' : null}
            inputProps={{ readOnly: isReadOnly }}
          /> */}
          <Autocomplete
            options={countries}
            getOptionLabel={option => option.country_name || option.label}
            noOptionsText={t('message:no-results')}
            isOptionEqualToValue={(option, value) =>
              option.code === value?.code || option?.code === value?.code
            }
            name="country"
            inputProps={{
              name: 'country',
              required: true,
              label: (
                <Typography variant="h4bold">{t('noun:country')}</Typography>
              ),
              type: 'text',
              ...getFieldProps('country', values, fieldErrors, touched, t),
              className: 'input-small ',
            }}
            onChange={(_, option) => {
              onChange({
                target: { name: 'country', value: option?.code },
              })
            }}
            value={countries.find(country => country.code === values.country)}
          />
        </div>
      )}
      <TextField
        name="stair"
        label={t('noun:stair')}
        {...getFieldProps('stair', values, fieldErrors, touched, t)}
        onChange={onChange}
        onBlur={onBlur}
        className={isSmall ? 'input-micro' : 'input-microXS'}
        color={isSaveAddressSuccess && values.stair ? 'success' : null}
        inputProps={{ readOnly: isReadOnly }}
      />
      <TextField
        name="floor"
        label={t('noun:floor')}
        {...getFieldProps('floor', values, fieldErrors, touched, t)}
        onChange={onChange}
        onBlur={onBlur}
        className={isSmall ? 'input-micro' : 'input-microXS'}
        color={isSaveAddressSuccess && values.floor ? 'success' : null}
        inputProps={{ readOnly: isReadOnly }}
      />
      <TextField
        name="letter"
        label={t('noun:letter')}
        {...getFieldProps('letter', values, fieldErrors, touched, t)}
        onChange={onChange}
        onBlur={onBlur}
        className={isSmall ? 'input-micro' : 'input-microXS'}
        color={isSaveAddressSuccess && values.letter ? 'success' : null}
        inputProps={{ readOnly: isReadOnly }}
      />
    </Box>
  )
}

AddressForm.propTypes = {
  isReadOnly: PropTypes.bool,
  values: PropTypes.object,
  fieldErrors: PropTypes.object,
  touched: PropTypes.object,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onChangeAutocompleteAddress: PropTypes.func.isRequired,
  onTouchAutocompleteAddress: PropTypes.func.isRequired,
  addressType: PropTypes.oneOf(Object.values(STEPS)),
  isSmall: PropTypes.bool,
  isSaveAddressSuccess: PropTypes.bool,
  countries: PropTypes.object,
}

export default AddressForm
